import React, { useState } from 'react';
import {
  Box,
  Grid,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  Alert,
  IconButton,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Paper,
  Divider,
  tableCellClasses,
} from '@mui/material';
import {
  PlayArrow as RunIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { runMigrateJournalEntries } from '../../store/reportStore/reportThunk';
import { toast } from 'react-toastify';
import styled_ from '@emotion/styled';

const StyledTableCell = styled_(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: '#eaecf0',
    color: '#000',
    border: '0.75px solid #d9d4d4',
    padding: '6px 12px',
    fontSize: '9pt',
    fontWeight: 600,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: '9pt',
    color: '#292d35',
    padding: '5px 12px',
    border: '0.75px solid #d9d4d4',
    backgroundColor: '#fff',
  },
}));

const MODULES = [
  { value: 'all', label: 'All Modules' },
  { value: 'invoices', label: 'Invoices' },
  { value: 'creditNotes', label: 'Credit Notes' },
  { value: 'debitNotes', label: 'Debit Notes' },
  { value: 'paymentReceipts', label: 'Payment Receipts' },
  { value: 'vendorPayments', label: 'Vendor Payments' },
  { value: 'expenses', label: 'Expenses' },
  { value: 'gstPayments', label: 'GST Payments' },
  { value: 'tdsPayments', label: 'TDS Payments' },
  { value: 'tcsPayments', label: 'TCS Payments' },
  { value: 'salaries', label: 'Salaries' },
  { value: 'dailyWages', label: 'Daily Wages' },
];

const ACTION_COLORS: Record<string, { bg: string; color: string }> = {
  CREATED: { bg: '#dcfce7', color: '#166534' },
  RELINKED: { bg: '#dbeafe', color: '#1e40af' },
  SKIPPED: { bg: '#f3f4f6', color: '#374151' },
  FAILED: { bg: '#fee2e2', color: '#991b1b' },
};

const MigrateJournalEntriesPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { selectedAccount } = useAppSelector((state) => state.auth);

  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [selectedModule, setSelectedModule] = useState('all');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingLive, setIsLoadingLive] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [filterAction, setFilterAction] = useState<string>('ALL');
  const [dryRunCompleted, setDryRunCompleted] = useState(false);

  // Reset dry run approval when params change
  const handleFromDateChange = (val: string) => {
    setFromDate(val);
    setDryRunCompleted(false);
  };
  const handleToDateChange = (val: string) => {
    setToDate(val);
    setDryRunCompleted(false);
  };
  const handleModuleChange = (val: string) => {
    setSelectedModule(val);
    setDryRunCompleted(false);
  };

  const handleDryRun = () => {
    if (!fromDate || !toDate) {
      toast.warn('Please select both From Date and To Date.');
      return;
    }

    setIsLoading(true);
    setResult(null);
    setDryRunCompleted(false);

    dispatch(runMigrateJournalEntries({
      fromDate,
      toDate,
      dryRun: true,
      module: selectedModule === 'all' ? undefined : selectedModule,
    }))
      .unwrap()
      .then((res) => {
        if (res.success) {
          setResult(res.data);
          setDryRunCompleted(true);
          toast.info(`Dry run complete. ${res.data.totals.found} records found.`);
        } else {
          toast.error(res.message || 'Dry run failed.');
        }
      })
      .catch((err) => {
        toast.error(err.message || 'Dry run failed.');
      })
      .finally(() => setIsLoading(false));
  };

  const handleLiveRun = () => {
    if (!fromDate || !toDate) return;

    const confirmed = window.confirm(
      'You are about to run a LIVE migration that will CREATE journal entries.\n\nThis cannot be undone. Are you sure?'
    );
    if (!confirmed) return;

    setIsLoadingLive(true);

    dispatch(runMigrateJournalEntries({
      fromDate,
      toDate,
      dryRun: false,
      module: selectedModule === 'all' ? undefined : selectedModule,
    }))
      .unwrap()
      .then((res) => {
        if (res.success) {
          setResult(res.data);
          setDryRunCompleted(false);
          toast.success(`Migration complete. ${res.data.totals.migrated} created, ${res.data.totals.failed} failed.`);
        } else {
          toast.error(res.message || 'Migration failed.');
        }
      })
      .catch((err) => {
        toast.error(err.message || 'Migration failed.');
      })
      .finally(() => setIsLoadingLive(false));
  };

  const filteredLog = result?.reportLog
    ? (filterAction === 'ALL' ? result.reportLog : result.reportLog.filter((r: any) => r.action === filterAction))
    : [];

  const moduleRows = result
    ? Object.entries(result.stats as Record<string, any>).filter(([mod]) => selectedModule === 'all' || mod === selectedModule)
    : [];

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Box sx={{ width: 'calc(100% - 7px)', height: 'calc(100vh - 70px)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }} className="c-card2">
          <Box sx={{ flex: 1, overflow: 'auto', '&::-webkit-scrollbar': { display: 'none' }, msOverflowStyle: 'none', scrollbarWidth: 'none' }}>

            {/* Header */}
            <Box sx={{
              background: 'linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)',
              color: '#374151',
              p: 2,
              borderRadius: 2,
              mb: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
            }}>
              <IconButton
                onClick={() => navigate('/reports/day-book')}
                size="small"
                sx={{
                  bgcolor: 'rgba(255,255,255,0.8)',
                  color: '#374151',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.9)', transform: 'translateX(-2px)' },
                  transition: 'all 0.2s ease',
                }}
              >
                <ArrowBackIcon fontSize="small" />
              </IconButton>
              <Box flex={1}>
                <Typography variant="h6" fontWeight={700} sx={{ fontSize: '1.1rem', lineHeight: 1.3, color: '#374151', mb: 0.3 }}>
                  Migrate Journal Entries
                </Typography>
                <Typography variant="caption" sx={{ color: '#374151', opacity: 0.8 }}>
                  Retroactively create journal entries for historical transactions. Always run a Dry Run first before going Live.
                </Typography>
              </Box>
            </Box>

            {/* Controls */}
            <Box sx={{ px: 3, py: 2 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item>
                  <TextField
                    label="From Date"
                    type="date"
                    size="small"
                    value={fromDate}
                    onChange={(e) => handleFromDateChange(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ minWidth: 160 }}
                  />
                </Grid>
                <Grid item>
                  <TextField
                    label="To Date"
                    type="date"
                    size="small"
                    value={toDate}
                    onChange={(e) => handleToDateChange(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ minWidth: 160 }}
                  />
                </Grid>
                <Grid item>
                  <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel>Module</InputLabel>
                    <Select
                      value={selectedModule}
                      label="Module"
                      onChange={(e) => handleModuleChange(e.target.value)}
                    >
                      {MODULES.map((m) => (
                        <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item>
                  <Button
                    variant="contained"
                    color="info"
                    startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : <RunIcon />}
                    onClick={handleDryRun}
                    disabled={isLoading || isLoadingLive}
                    size="small"
                    sx={{ fontWeight: 600, px: 3 }}
                  >
                    {isLoading ? 'Running...' : 'Dry Run'}
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                    variant="contained"
                    color="error"
                    startIcon={isLoadingLive ? <CircularProgress size={16} color="inherit" /> : <RunIcon />}
                    onClick={handleLiveRun}
                    disabled={!dryRunCompleted || isLoading || isLoadingLive}
                    size="small"
                    sx={{ fontWeight: 600, px: 3 }}
                  >
                    {isLoadingLive ? 'Migrating...' : 'Run Migration'}
                  </Button>
                </Grid>
              </Grid>

              {!dryRunCompleted && result === null && (
                <Alert severity="info" sx={{ mt: 1.5, fontSize: '0.8rem' }}>
                  Run a <strong>Dry Run</strong> first to preview which records will be migrated. The <strong>Run Migration</strong> button will be enabled after you verify the results.
                </Alert>
              )}

              {dryRunCompleted && (
                <Alert severity="warning" sx={{ mt: 1.5, fontSize: '0.8rem' }}>
                  Dry run verified. Review the results below, then click <strong>Run Migration</strong> to create journal entries. Changing dates or module will require a new dry run.
                </Alert>
              )}
            </Box>

            {/* Results */}
            {result && (
              <Box sx={{ px: 3, pb: 3 }}>
                <Divider sx={{ mb: 2 }} />

                {/* Run info */}
                <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                  <Chip label={result.dryRun ? 'DRY RUN' : 'LIVE'} color={result.dryRun ? 'info' : 'error'} size="small" sx={{ fontWeight: 700 }} />
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                    Account: <strong>{result.accountName}</strong>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                    Period: <strong>{result.fromDate}</strong> to <strong>{result.toDate}</strong>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                    Module: <strong>{result.module}</strong>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                    Time: <strong>{result.elapsedSeconds}s</strong>
                  </Typography>
                </Box>

                {/* Summary cards */}
                <Grid container spacing={1.5} sx={{ mb: 2.5 }}>
                  {[
                    { label: 'Found', value: result.totals.found, color: '#374151', bg: '#f9fafb' },
                    { label: 'Migrated', value: result.totals.migrated, color: '#166534', bg: '#dcfce7' },
                    { label: 'Relinked', value: result.totals.relinked, color: '#1e40af', bg: '#dbeafe' },
                    { label: 'Skipped', value: result.totals.skipped, color: '#92400e', bg: '#fef3c7' },
                    { label: 'Failed', value: result.totals.failed, color: '#991b1b', bg: '#fee2e2' },
                  ].map((card) => (
                    <Grid item key={card.label}>
                      <Paper elevation={0} sx={{ px: 2.5, py: 1.5, backgroundColor: card.bg, border: '1px solid #e5e7eb', borderRadius: 2, minWidth: 100, textAlign: 'center' }}>
                        <Typography variant="h5" fontWeight={700} sx={{ color: card.color, lineHeight: 1 }}>
                          {card.value}
                        </Typography>
                        <Typography variant="caption" sx={{ color: card.color, fontWeight: 500, fontSize: '0.75rem' }}>
                          {card.label}
                        </Typography>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>

                {/* Per-module table */}
                <Typography variant="body2" fontWeight={700} sx={{ mb: 1, fontSize: '0.85rem', color: '#374151' }}>
                  Module Breakdown
                </Typography>
                <Table size="small" sx={{ mb: 3, tableLayout: 'fixed', width: '100%' }}>
                  <colgroup>
                    <col style={{ width: '22%' }} />
                    <col style={{ width: '13%' }} />
                    <col style={{ width: '13%' }} />
                    <col style={{ width: '13%' }} />
                    <col style={{ width: '13%' }} />
                    <col style={{ width: '13%' }} />
                  </colgroup>
                  <TableHead>
                    <TableRow>
                      <StyledTableCell>Module</StyledTableCell>
                      <StyledTableCell style={{ textAlign: 'center' }}>Found</StyledTableCell>
                      <StyledTableCell style={{ textAlign: 'center' }}>Migrated</StyledTableCell>
                      <StyledTableCell style={{ textAlign: 'center' }}>Relinked</StyledTableCell>
                      <StyledTableCell style={{ textAlign: 'center' }}>Skipped</StyledTableCell>
                      <StyledTableCell style={{ textAlign: 'center' }}>Failed</StyledTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {moduleRows.map(([mod, s]: [string, any]) => (
                      <TableRow key={mod}>
                        <StyledTableCell sx={{ fontWeight: 600, textTransform: 'capitalize' }}>{mod}</StyledTableCell>
                        <StyledTableCell style={{ textAlign: 'center' }}>{s.found}</StyledTableCell>
                        <StyledTableCell style={{ textAlign: 'center', color: s.migrated > 0 ? '#166534' : undefined, fontWeight: s.migrated > 0 ? 700 : undefined }}>{s.migrated}</StyledTableCell>
                        <StyledTableCell style={{ textAlign: 'center', color: s.relinked > 0 ? '#1e40af' : undefined }}>{s.relinked}</StyledTableCell>
                        <StyledTableCell style={{ textAlign: 'center', color: '#92400e' }}>{s.skipped}</StyledTableCell>
                        <StyledTableCell style={{ textAlign: 'center', color: s.failed > 0 ? '#991b1b' : undefined, fontWeight: s.failed > 0 ? 700 : undefined }}>{s.failed}</StyledTableCell>
                      </TableRow>
                    ))}
                    {/* Totals row */}
                    <TableRow sx={{ backgroundColor: '#f1f5f9' }}>
                      <StyledTableCell sx={{ fontWeight: 700, backgroundColor: '#f1f5f9' }}>TOTAL</StyledTableCell>
                      <StyledTableCell style={{ textAlign: 'center', fontWeight: 700, backgroundColor: '#f1f5f9' }}>{result.totals.found}</StyledTableCell>
                      <StyledTableCell style={{ textAlign: 'center', fontWeight: 700, color: '#166534', backgroundColor: '#f1f5f9' }}>{result.totals.migrated}</StyledTableCell>
                      <StyledTableCell style={{ textAlign: 'center', fontWeight: 700, color: '#1e40af', backgroundColor: '#f1f5f9' }}>{result.totals.relinked}</StyledTableCell>
                      <StyledTableCell style={{ textAlign: 'center', fontWeight: 700, color: '#92400e', backgroundColor: '#f1f5f9' }}>{result.totals.skipped}</StyledTableCell>
                      <StyledTableCell style={{ textAlign: 'center', fontWeight: 700, color: result.totals.failed > 0 ? '#991b1b' : undefined, backgroundColor: '#f1f5f9' }}>{result.totals.failed}</StyledTableCell>
                    </TableRow>
                  </TableBody>
                </Table>

                {/* Report log */}
                {result.reportLog && result.reportLog.length > 0 && (
                  <>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                      <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.85rem', color: '#374151' }}>
                        Transaction Log ({filteredLog.length} records)
                      </Typography>
                      {(['ALL', 'CREATED', 'RELINKED', 'SKIPPED', 'FAILED'] as const).map((action) => (
                        <Chip
                          key={action}
                          label={action}
                          size="small"
                          onClick={() => setFilterAction(action)}
                          variant={filterAction === action ? 'filled' : 'outlined'}
                          sx={{
                            fontSize: '0.7rem',
                            height: '22px',
                            cursor: 'pointer',
                            ...(filterAction === action && action !== 'ALL' ? { backgroundColor: ACTION_COLORS[action]?.bg, color: ACTION_COLORS[action]?.color, fontWeight: 700 } : {}),
                          }}
                        />
                      ))}
                    </Box>

                    <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
                      <colgroup>
                        <col style={{ width: '8%' }} />
                        <col style={{ width: '13%' }} />
                        <col style={{ width: '10%' }} />
                        <col style={{ width: '9%' }} />
                        <col style={{ width: '14%' }} />
                        <col style={{ width: '9%' }} />
                        <col style={{ width: '9%' }} />
                        <col style={{ width: '28%' }} />
                      </colgroup>
                      <TableHead>
                        <TableRow>
                          <StyledTableCell>Action</StyledTableCell>
                          <StyledTableCell>Module</StyledTableCell>
                          <StyledTableCell>Voucher No.</StyledTableCell>
                          <StyledTableCell>Date</StyledTableCell>
                          <StyledTableCell>Party</StyledTableCell>
                          <StyledTableCell style={{ textAlign: 'right' }}>Amount</StyledTableCell>
                          <StyledTableCell>Type</StyledTableCell>
                          <StyledTableCell>Error / JE ID</StyledTableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredLog.map((entry: any, idx: number) => {
                          const actionStyle = ACTION_COLORS[entry.action] || {};
                          return (
                            <TableRow key={idx}>
                              <StyledTableCell>
                                <Chip
                                  label={entry.action}
                                  size="small"
                                  sx={{
                                    fontSize: '8pt',
                                    height: '20px',
                                    backgroundColor: actionStyle.bg,
                                    color: actionStyle.color,
                                    fontWeight: 700,
                                  }}
                                />
                              </StyledTableCell>
                              <StyledTableCell sx={{ textTransform: 'capitalize', fontSize: '8.5pt' }}>{entry.module}</StyledTableCell>
                              <StyledTableCell sx={{ fontSize: '8.5pt' }}>{entry.voucherNumber || '-'}</StyledTableCell>
                              <StyledTableCell sx={{ fontSize: '8.5pt' }}>{entry.voucherDate || '-'}</StyledTableCell>
                              <StyledTableCell sx={{ fontSize: '8.5pt', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{entry.partyName || '-'}</StyledTableCell>
                              <StyledTableCell style={{ textAlign: 'right', fontSize: '8.5pt' }}>{entry.amount ? Number(entry.amount).toLocaleString('en-IN', { minimumFractionDigits: 2 }) : '-'}</StyledTableCell>
                              <StyledTableCell sx={{ fontSize: '7.5pt', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{entry.voucherType}</StyledTableCell>
                              <StyledTableCell sx={{ fontSize: '7.5pt', color: entry.error ? '#991b1b' : '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {entry.error || entry.journalEntryId || '-'}
                              </StyledTableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </>
                )}
              </Box>
            )}
          </Box>
        </Box>
      </Grid>
    </Grid>
  );
};

export default MigrateJournalEntriesPage;
